import { Dialog, Tab, Transition } from "@headlessui/react";
import { ButtonDefault, ButtonPrimary, Input, Label } from "components/form";
import { useAuth } from "contexts/Auth/AuthContext";
import React, { Fragment, useEffect, useMemo, useState } from "react";
import { useAppSelector } from "store/hooks";
import UserService from "services/user.service";
import customToast from "components/Toast/ToastSuccess";
import { Spinner } from "components/spinner/Spinner";
import ImageUpload from "./ImageUpload";
import { useForm } from "react-hook-form";

export const UploadExistingMenuPopup = ({
  isOpen,
  closeModal,
  onSuccessfullyCreated,
}: {
  isOpen: boolean;
  closeModal: (data?: any) => void;
  onSuccessfullyCreated: () => void;
}) => {
  const {
    control,
    handleSubmit,
    getValues,
    setValue,
    trigger,
    reset,
    getFieldState,
    formState: { errors, isValid, isDirty },
  } = useForm({
    defaultValues: {
      fileUrl: "",
      fileName: "",
      url: "",
    },
    mode: "onChange",
  });
  const auth = useAuth();
  const [isUpdating, setIsUpdating] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const onSubmit = async (data: any, e: any) => {
    const submittedUrl = selectedIndex === 0 ? data.fileUrl : data.url;
    if (!submittedUrl) {
      customToast.error(
        selectedIndex === 0 ? "File URL is required" : "URL is required"
      );
      return;
    }

    setIsUpdating(true);
    try {
      await UserService.createUserRequest(auth?.getCurrentUser()?.userId, {
        requestType: 2,
        userRequestFiles: [
          {
            url: selectedIndex === 0 ? data.fileUrl : data.url,
          },
        ],
      });
      customToast.success(`DONE`);
      setIsUpdating(false);
      onSuccessfullyCreated();
      closeModal({
        requestCreated: true,
      });
    } catch (error) {
      setIsUpdating(false);
      customToast.error(`Failed to send a request. Please try again`);
    }
  };

  useEffect(() => {
    if (!isOpen) {
      reset();
    }
  }, [isOpen]);

  const tabChange = (index: number) => {
    setSelectedIndex(index);
  };

  return (
    <>
      <Transition appear show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={() => {}}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div
              className="fixed inset-0 bg-black bg-opacity-25 pointer-events-none"
              aria-hidden="true"
            />
          </Transition.Child>

          <div className="fixed inset-0">
            <div className="flex min-h-full items-center justify-center text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel
                  className="w-full max-w-[500px] transform overflow-hidden rounded-md 
                    bg-white text-left align-middle shadow-xl transition-all"
                >
                  <Dialog.Title
                    as="h3"
                    className="p-6 pb-0 font-bold text-neutral-80 text-22px"
                  >
                    Upload existing menu
                  </Dialog.Title>
                  <div className="tabs p-6">
                    <form onSubmit={handleSubmit(onSubmit)}>
                      <div className="p-5 bg-neutral-10 rounded-xl">
                        <Tab.Group
                          selectedIndex={selectedIndex}
                          onChange={tabChange}
                        >
                          <Tab.List className="flex">
                            <Tab className="font-bold text-base">
                              Photo or File
                            </Tab>
                            <Tab className="font-bold text-base">Url</Tab>
                          </Tab.List>
                          <Tab.Panels>
                            <Tab.Panel className="rounded-xl">
                              <ImageUpload
                                imageDefault={getValues("fileUrl")}
                                imageDefaultName={getValues("fileName")}
                                size={{
                                  width: "w-full",
                                }}
                                onSelect={(data) => {
                                  setValue("fileUrl", data?.fileUrl, {
                                    shouldDirty: true,
                                  });
                                  setValue("fileName", data?.fileName);
                                }}
                                isCropDisabled={true}
                              />
                            </Tab.Panel>
                            <Tab.Panel className="rounded-xl">
                              <Label name="URL" />
                              <Input
                                name="url"
                                control={control}
                                placeholder="Paste your existing menu URL here"
                                type="text"
                              />
                            </Tab.Panel>
                          </Tab.Panels>
                        </Tab.Group>
                      </div>
                      <div className="flex mt-8">
                        <div className="flex flex-col sm:flex-row ml-auto w-full sm:w-auto">
                          <ButtonDefault
                            size="small"
                            styles="!w-auto sm:mr-4 !font-medium min-w-[71px]"
                            onClick={() => closeModal()}
                          >
                            Cancel
                          </ButtonDefault>
                          <ButtonPrimary
                            type="submit"
                            size="small"
                            disabled={!isDirty}
                            styles="!font-medium mt-4 sm:mt-0"
                          >
                            {isUpdating && <Spinner />}
                            <span>
                              {(isUpdating && "Please wait...") || "Upload"}
                            </span>
                          </ButtonPrimary>
                        </div>
                      </div>
                    </form>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};
